import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Spinner, Modal } from 'react-bootstrap';
import { FaCog } from 'react-icons/fa';
import api from '../../../services/api';
import { fmt, fmtUnit, fmtDate } from '../utils/helpers';
import { TableExportBar } from '../shared/index';
import { exportExcel, exportObjectifCommercialExcel, rowsToHtml, copyHtmlToClipboard } from '../utils/exports';
import InfoTip from './InfoTip';

// Performance par commercial — réalisé vs objectif (mois sélectionné + cumul janvier → mois).
// Données : GET /decisionnel/objectif-commercial (agrégé côté SQL), détail au clic via /detail.

const INDS = [
  { key: 'ventes', label: 'Ventes',       color: '#6d4e36', info: 'Prix net des contrats (hors contrats ASSOCIATION si l\'option parts est active) + prix des parts d\'association. Date : réservation / date de la part. Objectif : goalV.' },
  { key: 'encRecouv', label: 'Enc_Recouv', color: '#1565c0', info: 'Somme des encaissements et du recouvrement (tous enc_supp), date de saisie. Exclus : IMPAYE, RECUPERE, A_NE_PAS_VERSER. Objectif : goalE + goalR.' },
  { key: 'liv',    label: 'Livraison',    color: '#6a1b9a', info: 'Prix des biens passés chez le notaire (signé client, signature en cours ou signé notaire). Date : la plus ancienne date notaire. Objectif : goalL.' },
];
const STATUTS = [
  { value: 'NOT_YET',  label: 'Pas encore signé' },
  { value: 'CHEZ',     label: 'Signé client' },
  { value: 'EN_COURS', label: 'Signature en cours' },
  { value: 'SIGNE',    label: 'Signé notaire' },
];
const MOIS_LABELS = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];

const pct   = (c) => c.o > 0 ? Math.ceil(c.r * 100 / c.o) : null;
const ecart = (c) => c.o > 0 ? c.r - c.o : 0;
const pctStyle = (p) => {
  if (p == null) return { color: '#bbb', background: 'transparent' };
  if (p >= 100)  return { color: '#1b5e20', background: '#e8f5e9' };
  if (p >= 70)   return { color: '#e65100', background: '#fff3e0' };
  return { color: '#c62828', background: '#ffebee' };
};

const TH  = { padding: '6px 8px', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: '#4a505c', background: '#f5f4f2', borderBottom: '2px solid #d4c8bc', whiteSpace: 'nowrap', textAlign: 'right' };
const TD  = { padding: '5px 8px', fontSize: '0.76rem', borderBottom: '1px solid #f0ebe4', whiteSpace: 'nowrap', textAlign: 'right' };
const chip = (active, color = '#6d4e36') => ({
  padding: '3px 10px', fontSize: '0.7rem', fontWeight: 600, borderRadius: 12, cursor: 'pointer',
  border: `1px solid ${active ? color : '#d4c8bc'}`, background: active ? color : '#fff', color: active ? '#fff' : '#777',
});

const PctPill = ({ p }) => (
  <span style={{ ...pctStyle(p), borderRadius: 10, padding: '1px 7px', fontWeight: 700, fontSize: '0.7rem' }}>
    {p == null ? '—' : `${p}%`}
  </span>
);

const rowStyle = (kind) => kind === 'total'
  ? { background: '#f5ede4', fontWeight: 700 }
  : kind === 'autres' ? { background: '#faf9f7', fontStyle: 'italic', color: '#666' } : {};

// Commerciaux regroupés manuellement dans « Autres » (réglage persisté par navigateur) : { id: nom }
const AUTRES_KEY = 'dec_objcom_autres';
const loadAutresMan = () => { try { return JSON.parse(localStorage.getItem(AUTRES_KEY) || '{}'); } catch { return {}; } };

// Déplace le réalisé et l'objectif des commerciaux choisis vers « Autres ». TOTAL inchangé.
const applyAutresMan = (rows, ids) => {
  if (!ids.size) return rows;
  const moved = rows.filter(r => r.kind === 'com' && ids.has(r.id));
  if (!moved.length) return rows;
  const autres = rows.find(r => r.kind === 'autres');
  if (!autres) return rows;
  const merged = { ...autres, commercial: `Autres (+${moved.length} regroupé${moved.length > 1 ? 's' : ''})` };
  for (const i of INDS) {
    merged[i.key] = {
      m: { r: autres[i.key].m.r, o: autres[i.key].m.o },
      c: { r: autres[i.key].c.r, o: autres[i.key].c.o },
    };
    for (const r of moved) for (const per of ['m', 'c']) for (const fld of ['r', 'o']) merged[i.key][per][fld] += r[i.key][per][fld];
  }
  return rows.filter(r => !(r.kind === 'com' && ids.has(r.id))).map(r => (r.kind === 'autres' ? merged : r));
};

const applyAutresManDetails = (details, ids, annee) => {
  const out = new Map();
  for (const d of details) {
    const moved = d.comId !== 'autres' && ids.has(d.comId);
    const comId = moved ? 'autres' : d.comId;
    const k = `${d.projetId}|${comId}|${d.mois}`;
    if (!out.has(k)) {
      out.set(k, { ...d, annee, comId, commercial: moved ? 'Autres' : d.commercial,
        ventes: { r: 0, o: 0 }, encRecouv: { r: 0, o: 0 }, liv: { r: 0, o: 0 } });
    }
    const e = out.get(k);
    for (const i of INDS) { e[i.key].r += d[i.key].r; e[i.key].o += d[i.key].o; }
  }
  return [...out.values()].sort((a, b) => a.ville.localeCompare(b.ville, 'fr') || a.projet.localeCompare(b.projet, 'fr')
    || (a.comId === 'autres') - (b.comId === 'autres') || a.commercial.localeCompare(b.commercial, 'fr') || a.mois - b.mois);
};

const ObjectifCommercialSection = ({ projetIds, projetLabel, finDate, unit }) => {
  const initMois = (finDate || new Date().toISOString()).slice(0, 7);
  const [periode,   setPeriode]   = useState(initMois);   // YYYY-MM
  const [partsAsso, setPartsAsso] = useState(true);
  const [titre,     setTitre]     = useState('tout');
  const [statuts,   setStatuts]   = useState(STATUTS.map(s => s.value));
  const [vue,       setVue]       = useState('synthese'); // synthese | detail
  const [perSynth,  setPerSynth]  = useState('c');        // m | c
  const [ongInd,    setOngInd]    = useState('ventes');
  const [regroup,   setRegroup]   = useState('tous');     // tous (cumulé) | ville | projet
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');
  const [detail,    setDetail]    = useState(null);       // { title, ind, rows, loading }
  const [reglages,  setReglages]  = useState(false);
  const [autresMan, setAutresMan] = useState(loadAutresMan);
  const autresIds = useMemo(() => new Set(Object.keys(autresMan)), [autresMan]);
  const toggleAutresMan = (id, nom) => setAutresMan(prev => {
    const next = { ...prev };
    if (next[id]) delete next[id]; else next[id] = nom;
    try { localStorage.setItem(AUTRES_KEY, JSON.stringify(next)); } catch {}
    return next;
  });
  const resetAutresMan = () => { setAutresMan({}); try { localStorage.removeItem(AUTRES_KEY); } catch {} };
  const reqId = useRef(0);

  // Le mois suit la date de fin des filtres globaux
  useEffect(() => { if (finDate) setPeriode(finDate.slice(0, 7)); }, [finDate]);

  const [annee, mois] = periode.split('-').map(Number);
  const params = useMemo(() => {
    const p = { annee, mois, partsAsso: partsAsso ? '1' : '0', titre };
    if (projetIds.length) p.projetIds = projetIds.join(',');
    if (statuts.length < STATUTS.length) p.statuts = statuts.join(',');
    return p;
  }, [annee, mois, partsAsso, titre, statuts, projetIds]);

  useEffect(() => {
    if (!annee || !mois) return;
    const id = ++reqId.current;
    setLoading(true); setError('');
    api.get('/decisionnel/objectif-commercial', { params })
      .then(r => { if (id === reqId.current) setData(r.data); })
      .catch(e => { if (id === reqId.current && e?.response?.status !== 401) setError(e?.response?.data?.message || 'Erreur chargement'); })
      .finally(() => { if (id === reqId.current) setLoading(false); });
  }, [params, annee, mois]);

  // grp = groupe affiché (une ville ou un projet) : le détail est alors limité à ses projets
  const openDetail = (row, ind, per, grp) => {
    const indLabel = INDS.find(i => i.key === ind)?.label;
    const perLabel = per === 'm' ? `${MOIS_LABELS[mois - 1]} ${annee}` : `Cumul janv. → ${MOIS_LABELS[mois - 1].toLowerCase()} ${annee}`;
    const title = [indLabel, grp?.titre, row.commercial, perLabel].filter(Boolean).join(' — ');
    const detailParams = { ...params, ind, row: row.id, periode: per };
    if (grp?.projetIds?.length) detailParams.projetIds = grp.projetIds.join(',');
    if (row.id === 'autres' && autresIds.size) detailParams.autresCom = [...autresIds].join(',');
    setDetail({ title, ind, rows: [], loading: true });
    api.get('/decisionnel/objectif-commercial/detail', { params: detailParams })
      .then(r => setDetail(d => d && { ...d, rows: r.data, loading: false }))
      .catch(() => setDetail(d => d && { ...d, loading: false, error: true }));
  };

  const rawRows = data?.rows || [];
  const rows = applyAutresMan(rawRows, autresIds);
  const projetGroups = (data?.projets || []).map(g => ({ key: `p${g.projetId}`, projetIds: [g.projetId], titre: g.projet, rows: applyAutresMan(g.rows, autresIds) }));
  const villeGroups  = (data?.villes  || []).map(g => ({ key: `v${g.villeId}`,  projetIds: g.projetIds,   titre: g.ville,  rows: applyAutresMan(g.rows, autresIds) }));
  const groups = regroup === 'projet' ? projetGroups : regroup === 'ville' ? villeGroups : [{ key: 'all', titre: null, rows }];
  // Commerciaux proposables au regroupement : affectés présents + déjà regroupés (même absents de la période)
  const comOptions = [
    ...rawRows.filter(r => r.kind === 'com').map(r => ({ id: r.id, nom: r.commercial })),
    ...Object.entries(autresMan).filter(([id]) => !rawRows.some(r => r.id === id)).map(([id, nom]) => ({ id, nom })),
  ].sort((a, b) => a.nom.localeCompare(b.nom, 'fr'));
  const moisLabel = `${MOIS_LABELS[mois - 1] || ''} ${annee || ''}`;

  /* ── Exports ── */
  const exportRows = () => groups.flatMap(g => {
    const proj = g.titre ? { [regroup === 'ville' ? 'Ville' : 'Projet']: g.titre } : {};
    if (vue === 'synthese') {
      return g.rows.map(r => {
        const o = { ...proj, Commercial: r.commercial };
        for (const i of INDS) { const c = r[i.key][perSynth]; o[`${i.label} réalisé`] = Math.round(c.r); o[`${i.label} objectif`] = Math.round(c.o); o[`${i.label} %`] = pct(c) != null ? `${pct(c)}%` : '—'; }
        return o;
      });
    }
    return g.rows.map(r => ({ r, c: r[ongInd] })).map(({ r, c }) => ({
      ...proj, Commercial: r.commercial,
      [`${moisLabel} réalisé`]: Math.round(c.m.r), [`${moisLabel} objectif`]: Math.round(c.m.o), [`${moisLabel} écart`]: Math.round(ecart(c.m)), [`${moisLabel} %`]: pct(c.m) != null ? `${pct(c.m)}%` : '—',
      'Cumul réalisé': Math.round(c.c.r), 'Cumul objectif': Math.round(c.c.o), 'Cumul écart': Math.round(ecart(c.c)), 'Cumul %': pct(c.c) != null ? `${pct(c.c)}%` : '—',
    }));
  });
  // Excel : Synthèse générale, par ville, par projet (mois + cumul) + Détails mensuels à plat
  const exportWorkbook = () => {
    const notaire = statuts.length === STATUTS.length ? 'tous' : STATUTS.filter(s => statuts.includes(s.value)).map(s => s.label).join(', ') || 'aucun';
    const titreTxt = { tout: 'Tout', avec: 'Titrés', sans: 'Non titrés' }[titre];
    exportObjectifCommercialExcel({
      general: rows, villes: villeGroups, projets: projetGroups,
      details: applyAutresManDetails(data?.details || [], autresIds, annee),
      inds: INDS, moisLabel,
      cumulLabel: `Cumul janvier → ${MOIS_LABELS[mois - 1]?.toLowerCase()} ${annee}`,
      filtres: `Projets : ${projetLabel || 'tous'} · Mois : ${moisLabel} · Parts d'association : ${partsAsso ? 'oui' : 'non'} · Titrés : ${titreTxt} · Statut notaire : ${notaire} · Montants en DH`,
      filename: `performance-commercial-${periode}`,
    });
  };

  /* ── Cellule montant cliquable ── */
  const Amount = ({ row, ind, per, value, strong, grp }) => (
    <span onClick={value ? () => openDetail(row, ind, per, grp) : undefined}
      title={value ? 'Voir le détail' : undefined}
      style={{ cursor: value ? 'pointer' : 'default', textDecoration: value ? 'underline dotted' : 'none', fontWeight: strong ? 700 : 600 }}>
      {fmtUnit(value, unit)}
    </span>
  );

  return (
    <div style={{ padding: '12px 16px' }}>
      {/* ── Paramètres ── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 14px', marginBottom: 10, padding: '8px 12px', background: '#fdf8f4', border: '1px solid #e8ddd4', borderRadius: 8 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.75rem', color: '#555', fontWeight: 600 }}>
          Mois
          <input type="month" value={periode} onChange={e => e.target.value && setPeriode(e.target.value)}
            style={{ border: '1px solid #d4c8bc', borderRadius: 6, padding: '2px 6px', fontSize: '0.75rem' }} />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.75rem', color: '#555', cursor: 'pointer' }}>
          <input type="checkbox" checked={partsAsso} onChange={e => setPartsAsso(e.target.checked)} style={{ accentColor: '#6d4e36' }} />
          Parts vendues si association
          <InfoTip text="Activé : les contrats de type ASSOCIATION sont remplacés par leurs parts vendues (prix et commercial de chaque part). Désactivé : tous les contrats, sans les parts." />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.75rem', color: '#555', fontWeight: 600 }}>
          Titrés
          <select value={titre} onChange={e => setTitre(e.target.value)} style={{ border: '1px solid #d4c8bc', borderRadius: 6, padding: '2px 6px', fontSize: '0.75rem' }}>
            <option value="tout">Tout</option>
            <option value="avec">Titrés (TF renseigné)</option>
            <option value="sans">Non titrés</option>
          </select>
          <InfoTip text="S'applique aux ventes et aux livraisons, pas aux encaissements." />
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.75rem', color: '#555', fontWeight: 600, marginRight: 2 }}>Notaire</span>
          {STATUTS.map(s => {
            const on = statuts.includes(s.value);
            return <button key={s.value} style={chip(on, '#6a1b9a')}
              onClick={() => setStatuts(prev => on ? prev.filter(v => v !== s.value) : [...prev, s.value])}>{s.label}</button>;
          })}
        </div>
      </div>

      {/* ── Vue + exports ── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <button style={chip(regroup === 'tous', '#00695c')} onClick={() => setRegroup('tous')}>Tous les projets</button>
        <button style={chip(regroup === 'ville', '#00695c')} onClick={() => setRegroup('ville')}>
          Par ville{data?.villes ? ` (${data.villes.length})` : ''}
        </button>
        <button style={chip(regroup === 'projet', '#00695c')} onClick={() => setRegroup('projet')}>
          Par projet{data?.projets ? ` (${data.projets.length})` : ''}
        </button>
        <span style={{ width: 1, height: 18, background: '#e0d8d0', margin: '0 4px' }} />
        <button style={chip(vue === 'synthese')} onClick={() => setVue('synthese')}>Synthèse</button>
        <button style={chip(vue === 'detail')}   onClick={() => setVue('detail')}>Détail par indicateur</button>
        <span style={{ width: 1, height: 18, background: '#e0d8d0', margin: '0 4px' }} />
        {vue === 'synthese' ? (<>
          <button style={chip(perSynth === 'm', '#4a505c')} onClick={() => setPerSynth('m')}>{moisLabel}</button>
          <button style={chip(perSynth === 'c', '#4a505c')} onClick={() => setPerSynth('c')}>Cumul janv. → {MOIS_LABELS[mois - 1]?.toLowerCase()}</button>
        </>) : INDS.map(i => (
          <button key={i.key} style={chip(ongInd === i.key, i.color)} onClick={() => setOngInd(i.key)}>{i.label}</button>
        ))}
        <span style={{ flex: 1 }} />
        <button style={chip(reglages, '#546e7a')} onClick={() => setReglages(v => !v)} title="Composition de « Autres » et regroupement de commerciaux">
          <FaCog size={10} style={{ marginRight: 4, verticalAlign: -1 }} />Réglages{autresIds.size ? ` (${autresIds.size})` : ''}
       
        </button>
        {!loading && rows.length > 0 && (
          <TableExportBar
            onExcel={exportWorkbook}
            onCopy={() => { const { html, plain } = rowsToHtml(exportRows()); copyHtmlToClipboard(html, plain); }} />
        )}
      </div>

      {reglages && data && (
        <AutresReglages data={data} rawRows={rawRows} unit={unit} autresMan={autresMan} autresIds={autresIds}
          comOptions={comOptions} onToggle={toggleAutresMan} onReset={resetAutresMan} />
      )}

      {error && <div style={{ color: '#c62828', fontSize: '0.8rem', padding: 8 }}>{error}</div>}
      {loading && !data ? <div style={{ textAlign: 'center', padding: 20 }}><Spinner /></div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, opacity: loading ? 0.5 : 1, transition: 'opacity 0.2s' }}>
        {groups.length === 0 && <div style={{ color: '#999', fontSize: '0.8rem', padding: 12, textAlign: 'center' }}>Aucune donnée pour cette période.</div>}
        {groups.map(g => (
        <div key={g.key}>
        {g.titre && (
          <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#00695c', margin: '0 0 5px 2px', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 4, height: 14, background: '#00695c', borderRadius: 2 }} />{g.titre}
          </div>
        )}
        <div style={{ overflowX: 'auto', border: '1px solid #e8ddd4', borderRadius: 8 }}>
          {vue === 'synthese' ? (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...TH, textAlign: 'left' }} rowSpan={2}>Commercial</th>
                  {INDS.map(i => (
                    <th key={i.key} colSpan={2} style={{ ...TH, textAlign: 'center', color: i.color, borderLeft: '1px solid #e8ddd4' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center' }}>{i.label}<InfoTip text={i.info} /></span>
                    </th>
                  ))}
                </tr>
                <tr>
                  {INDS.map(i => (
                    <React.Fragment key={i.key}>
                      <th style={{ ...TH, borderLeft: '1px solid #e8ddd4' }}>Réalisé / Obj.</th>
                      <th style={TH}>%</th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {g.rows.map(r => (
                  <tr key={r.id} style={rowStyle(r.kind)}>
                    <td style={{ ...TD, textAlign: 'left' }}>{r.commercial}</td>
                    {INDS.map(i => {
                      const c = r[i.key][perSynth];
                      return (
                        <React.Fragment key={i.key}>
                          <td style={{ ...TD, borderLeft: '1px solid #f0ebe4', color: i.color }}>
                            <Amount row={r} ind={i.key} per={perSynth} value={c.r} strong={r.kind === 'total'} grp={g} />
                            <span style={{ color: '#bbb', fontSize: '0.66rem', marginLeft: 4 }}>/ {c.o > 0 ? fmtUnit(c.o, unit) : '—'}</span>
                          </td>
                          <td style={TD}><PctPill p={pct(c)} /></td>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...TH, textAlign: 'left' }} rowSpan={2}>Commercial</th>
                  <th colSpan={4} style={{ ...TH, textAlign: 'center', borderLeft: '1px solid #e8ddd4' }}>{moisLabel}</th>
                  <th colSpan={4} style={{ ...TH, textAlign: 'center', borderLeft: '1px solid #e8ddd4' }}>Cumul janv. → {MOIS_LABELS[mois - 1]?.toLowerCase()}</th>
                </tr>
                <tr>
                  {['m', 'c'].map(per => (
                    <React.Fragment key={per}>
                      <th style={{ ...TH, borderLeft: '1px solid #e8ddd4' }}>Réalisé</th>
                      <th style={TH}>Objectif</th>
                      <th style={TH}>Écart</th>
                      <th style={TH}>%</th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {g.rows.map(r => {
                  const ind = ongInd;
                  const c = r[ind];
                  return (
                    <tr key={r.id} style={rowStyle(r.kind)}>
                      <td style={{ ...TD, textAlign: 'left' }}>{r.commercial}</td>
                      {['m', 'c'].map(per => {
                        const e = ecart(c[per]);
                        return (
                          <React.Fragment key={per}>
                            <td style={{ ...TD, borderLeft: '1px solid #f0ebe4' }}><Amount row={r} ind={ind} per={per} value={c[per].r} strong={r.kind === 'total'} grp={g} /></td>
                            <td style={{ ...TD, color: '#888' }}>{c[per].o > 0 ? fmtUnit(c[per].o, unit) : '—'}</td>
                            <td style={{ ...TD, color: e < 0 ? '#c62828' : e > 0 ? '#2e7d32' : '#bbb' }}>{e ? `${e > 0 ? '+' : ''}${fmtUnit(e, unit)}` : '—'}</td>
                            <td style={TD}><PctPill p={pct(c[per])} /></td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        </div>
        ))}
        </div>
      )}
      <div style={{ fontSize: '0.66rem', color: '#999', marginTop: 6 }}>
        « Autres » : ventes et encaissements dont le commercial n'est pas affecté au projet (ou absent) — objectif 0. « Tous les projets » cumule les projets sélectionnés ; « Par ville » / « Par projet » affichent un tableau par ville / par projet. Projets CM3 / CM3 SOCIAL exclus des ventes et livraisons. Cliquer sur un montant pour le détail.
      </div>

      {/* ── Détail au clic ── */}
      <Modal show={!!detail} onHide={() => setDetail(null)} size="xl" scrollable>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '0.95rem' }}>{detail?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 0 }}>
          {detail?.loading ? <div style={{ textAlign: 'center', padding: 30 }}><Spinner /></div>
           : detail?.error ? <div style={{ color: '#c62828', padding: 16 }}>Erreur chargement du détail</div>
           : <DetailLignes detail={detail} />}
        </Modal.Body>
      </Modal>
    </div>
  );
};

/* ── Panneau Réglages : composition de « Autres » + regroupement manuel ── */
const AutresReglages = ({ data, rawRows, unit, autresMan, autresIds, comOptions, onToggle, onReset }) => {
  const RAISON = { sans_commercial: 'Sans commercial', non_affecte: 'Non affecté au projet', manuel: 'Regroupé manuellement' };
  const compo = [
    ...(data.autresDetail || []).map(e => ({ ...e, cle: `a-${e.comId ?? 'none'}` })),
    ...rawRows.filter(r => r.kind === 'com' && autresIds.has(r.id)).map(r => ({
      cle: `m-${r.id}`, commercial: r.commercial, raison: 'manuel', projets: [],
      ventes: { c: r.ventes.c.r }, encRecouv: { c: r.encRecouv.c.r }, liv: { c: r.liv.c.r },
    })),
  ];
  const box = { flex: '1 1 380px', background: '#fff', border: '1px solid #e8ddd4', borderRadius: 8, padding: '8px 10px', minWidth: 0 };
  const h = { fontSize: '0.72rem', fontWeight: 700, color: '#4a505c', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6, display: 'flex', alignItems: 'center' };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 10, padding: 10, background: '#f7f9fa', border: '1px solid #cfd8dc', borderRadius: 8 }}>
      <div style={box}>
        <div style={h}>Composition de « Autres » — cumul<InfoTip text="Réalisé attribué à « Autres » : ventes et encaissements sans commercial, ou dont le commercial n'est pas affecté au projet (table worksat), plus les commerciaux regroupés manuellement. Objectif 0 sauf pour les regroupés manuellement." /></div>
        {compo.length === 0 ? <div style={{ fontSize: '0.75rem', color: '#999' }}>« Autres » est vide pour cette période.</div> : (
          <div style={{ overflowX: 'auto', maxHeight: 260, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={{ ...TH, textAlign: 'left', position: 'sticky', top: 0 }}>Commercial</th>
                <th style={{ ...TH, textAlign: 'left', position: 'sticky', top: 0 }}>Raison</th>
                <th style={{ ...TH, textAlign: 'left', position: 'sticky', top: 0 }}>Projets</th>
                {INDS.map(i => <th key={i.key} style={{ ...TH, color: i.color, position: 'sticky', top: 0 }}>{i.label}</th>)}
              </tr></thead>
              <tbody>
                {compo.map(e => (
                  <tr key={e.cle}>
                    <td style={{ ...TD, textAlign: 'left', fontWeight: 600 }}>{e.commercial}</td>
                    <td style={{ ...TD, textAlign: 'left', color: e.raison === 'manuel' ? '#546e7a' : e.raison === 'sans_commercial' ? '#c62828' : '#e65100', fontSize: '0.7rem' }}>{RAISON[e.raison]}</td>
                    <td style={{ ...TD, textAlign: 'left', fontSize: '0.7rem', color: '#777', whiteSpace: 'normal' }}>{e.projets.join(', ') || '—'}</td>
                    {INDS.map(i => <td key={i.key} style={TD}>{e[i.key].c ? fmtUnit(e[i.key].c, unit) : '—'}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div style={{ ...box, flex: '0 1 300px' }}>
        <div style={h}>Regrouper dans « Autres »<InfoTip text="Coché : le commercial n'a plus de ligne propre ; son réalisé et son objectif passent dans « Autres ». Le TOTAL ne change pas. Réglage mémorisé sur ce navigateur ; appliqué aux tableaux, au détail et à l'Excel." /></div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, maxHeight: 230, overflowY: 'auto' }}>
          {comOptions.length === 0 && <span style={{ fontSize: '0.75rem', color: '#999' }}>Aucun commercial affecté sur la période.</span>}
          {comOptions.map(c => (
            <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.76rem', color: '#444', cursor: 'pointer' }}>
              <input type="checkbox" checked={!!autresMan[c.id]} onChange={() => onToggle(c.id, c.nom)} style={{ accentColor: '#546e7a' }} />
              {c.nom}
            </label>
          ))}
        </div>
        {autresIds.size > 0 && (
          <button onClick={onReset} style={{ marginTop: 8, fontSize: '0.7rem', color: '#2e7d32', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600 }}>
            ↺ Tout réafficher
          </button>
        )}
      </div>
    </div>
  );
};

const DetailLignes = ({ detail }) => {
  if (!detail) return null;
  const isVente = detail.ind === 'ventes' || detail.ind === 'liv';
  const cols = isVente
    ? [['date', 'Date', v => fmtDate(v)], ['projet', 'Projet'], ['numDoss', 'N° dossier'], ['description', 'Bien'],
       ['rowType', 'Type', (v, l) => v === 'association' ? `Part ${l.part ?? ''}%` : 'Contrat'], ['clients', 'Client(s)'], ['commercial', 'Commercial'], ['montant', 'Montant', v => fmt(v)]]
    : [['date', 'Saisie', v => fmtDate(v)], ['dateReglement', 'Règlement', v => fmtDate(v)], ['projet', 'Projet'], ['tirePar', 'Tiré par'],
       ['modeRegl', 'Mode'], ['nature', 'Nature'], ['statut', 'Statut'], ['commercial', 'Commercial'], ['montant', 'Montant', v => fmt(v)]];
  const total = detail.rows.reduce((s, l) => s + l.montant, 0);
  const toExport = () => detail.rows.map(l => Object.fromEntries(cols.map(([k, label, f]) => [label, k === 'montant' ? l.montant : (f ? f(l[k], l) : l[k])])));
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', gap: 10, borderBottom: '1px solid #eee' }}>
        <span style={{ fontSize: '0.78rem', color: '#555' }}>{detail.rows.length} ligne{detail.rows.length !== 1 ? 's' : ''} · Total <b>{fmt(total)}</b> DH</span>
        <span style={{ flex: 1 }} />
        {detail.rows.length > 0 && (
          <TableExportBar onExcel={() => exportExcel(toExport(), 'detail-performance-commercial')}
            onCopy={() => { const { html, plain } = rowsToHtml(toExport()); copyHtmlToClipboard(html, plain); }} />
        )}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>{cols.map(([k, label]) => <th key={k} style={{ ...TH, textAlign: k === 'montant' ? 'right' : 'left', position: 'sticky', top: 0 }}>{label}</th>)}</tr></thead>
        <tbody>
          {detail.rows.map((l, idx) => (
            <tr key={idx}>
              {cols.map(([k, , f]) => <td key={k} style={{ ...TD, textAlign: k === 'montant' ? 'right' : 'left', whiteSpace: k === 'clients' || k === 'description' ? 'normal' : 'nowrap' }}>{f ? f(l[k], l) : l[k]}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

export default ObjectifCommercialSection;
